import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, setDoc, serverTimestamp, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { apiUrl } from '../utils/apiBase';
import { TAKI_URUN_TIPLERI } from '../utils/takiUrunTipleri';
import {
  buildUrunCreatePayload,
  buildUrunEkleTrendyolShipmentBase,
  generateUrunBarkod,
  resolveUkbaTrendyolBrandId,
  SABIT_KATEGORI,
  UKBA_GOLD_TRENDYOL_MARKA_ADI
} from '../utils/urunCreatePayload';
import { toast } from 'react-toastify';
import {
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Autocomplete,
  Fab,
  Stack,
} from '@mui/material';
import TrendyolProductInfoForm from '../components/trendyol/TrendyolProductInfoForm';
import { rejectDeprecatedTestTrendyolBrandId } from '../utils/trendyolBrandIds';

import {
  Inventory2 as InventoryIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

/** Trendyol marketplace kargo şirketi ID’leri (satıcı paneli / API listesi ile doğrulayın). */
const TRENDYOL_URUN_EKLE_CARGO_OPTIONS = [
  { id: '4', label: 'Yurtiçi Kargo' },
  { id: '8', label: 'MNG Kargo' },
  { id: '7', label: 'Aras Kargo' },
  { id: '9', label: 'Sürat Kargo' },
  { id: '19', label: 'PTT Kargo' },
  { id: '694', label: 'Hepsijet' },
  { id: '38', label: 'Kolay Gelsin' }
];
const TRENDYOL_URUN_EKLE_DEFAULT_CARGO_COMPANY_ID = '4';

const GORSEL_SLOT_COUNT = 5;

const makeEmptyImageSlots = () =>
  Array.from({ length: GORSEL_SLOT_COUNT }, () => ({
    file: null,
    preview: null
  }));

const accentOrange = '#ea580c';
const headerGradient = 'linear-gradient(90deg, #0f172a 0%, #1e293b 38%, #7c2d12 72%, #c2410c 100%)';
const buttonGradient = 'linear-gradient(90deg, #1c1917 0%, #431407 42%, #9a3412 78%, #ea580c 100%)';
const sectionIconSx = { color: accentOrange, fontSize: 24 };

/** Sayı alanlarındaki yukarı/aşağı (spinner) oklarını kaldırır */
const hideNumberSpinners = {
  '& input[type=number]': {
    MozAppearance: 'textfield'
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  }
};

const fieldSx = {
  flex: '1 1 120px',
  minWidth: 0,
  maxWidth: 220,
  ...hideNumberSpinners,
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    fontSize: '0.875rem',
    minHeight: 38,
    py: 0
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '0.875rem',
    py: '7px'
  },
  '& .MuiSelect-select': {
    fontSize: '0.875rem',
    py: '7px',
    minHeight: '1.25em'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.78rem',
    color: '#64748b',
    fontWeight: 600
  },
  '& .MuiInputAdornment-root': {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#475569'
  }
};

/** Genel bilgiler üst satırı: 8 eşit kolon grid hücreleri (dar hücrede etiket kesilmesin) */
const genelBilgilerGridFieldSx = {
  ...fieldSx,
  flex: 'none',
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  '& .MuiFormControl-root': { overflow: 'visible', width: '100%' },
  '& .MuiInputBase-root': { overflow: 'visible' },
  '& .MuiOutlinedInput-root': {
    ...(fieldSx['& .MuiOutlinedInput-root'] || {}),
    overflow: 'visible'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    overflow: 'visible'
  },
  '& .MuiInputLabel-root': {
    ...(fieldSx['& .MuiInputLabel-root'] || {}),
    zIndex: 1,
    overflow: 'visible',
    pointerEvents: 'auto',
    '&.MuiInputLabel-shrink': {
      maxWidth: 'none'
    }
  },
  '& .MuiSelect-select': {
    ...(fieldSx['& .MuiSelect-select'] || {}),
    overflow: 'visible'
  },
  '& .MuiOutlinedInput-root input, & .MuiOutlinedInput-root textarea': {
    textOverflow: 'ellipsis'
  }
};

/** Milyem / işçilik: yalnızca rakam ve en fazla bir ondalık ayırıcı */
function filterDecimalNumber(raw) {
  let s = String(raw).replace(/[^\d.]/g, '');
  const i = s.indexOf('.');
  if (i !== -1) {
    s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  }
  return s;
}

/** Harf + boşluk (örn. KOLYE UCU), Türkçe büyük harf */
function filterLettersSpacesUpperTR(raw) {
  const cleaned = String(raw).replace(/[^\p{L}\s]/gu, '');
  return cleaned.toLocaleUpperCase('tr-TR');
}

/** Harf + rakam + boşluk (örn. 4 MM, 45 CM), Türkçe büyük harf */
function filterAlnumSpacesUpperTR(raw) {
  const cleaned = String(raw).replace(/[^\p{L}\p{N}\s]/gu, '');
  return cleaned.toLocaleUpperCase('tr-TR');
}

const ImageUploadBox = ({
  label,
  preview,
  onChange,
  onRemove,
  draggable,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  isDragOver,
  isDragging,
  onSwapLeft,
  onSwapRight,
  showSwapLeft,
  showSwapRight,
  compact = false,
  stretchInRow = false
}) => {
  const boxPx = compact ? 84 : 156;
  const iconFont = compact ? 20 : 36;
  const labelFont = compact ? '0.635rem' : '0.8rem';
  const navIcon = compact ? 14 : 20;

  const sizingSx =
    stretchInRow && compact
      ? {
          flex: '1 1 0%',
          flexBasis: 0,
          width: '100%',
          maxWidth: 'none',
          minWidth: 0,
          height: 'unset',
          minHeight: 0,
          aspectRatio: '1'
        }
      : {
          flex: '0 0 auto',
          width: boxPx,
          minWidth: boxPx,
          maxWidth: boxPx,
          height: boxPx,
          minHeight: boxPx
        };

  return (
  <Box
    className="ue-image-slot"
    draggable={Boolean(draggable && preview)}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    sx={{
      ...sizingSx,
      border: isDragOver ? `2px solid ${accentOrange}` : '2px dashed #d4d4d8',
      borderRadius: compact ? '8px' : '10px',
      backgroundColor: isDragOver ? '#fff7ed' : compact ? '#ffffff' : '#faf7f2',
      cursor: preview ? 'grab' : 'pointer',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      transition: '0.2s',
      opacity: isDragging ? 0.55 : 1,
      '&:hover': { borderColor: accentOrange, backgroundColor: '#fff7ed' },
      ...(preview ? { '&:active': { cursor: 'grabbing' } } : {})
    }}
  >
    {preview ? (
      <>
        <img src={preview} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: compact ? 3 : 4, pointerEvents: 'none', userSelect: 'none' }} />
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            position: 'absolute',
            top: compact ? 1 : 2,
            right: compact ? 1 : 2,
            backgroundColor: 'rgba(255,255,255,0.9)',
            padding: compact ? '1px' : '2px',
            '&:hover': { backgroundColor: '#fff' }
          }}
          size="small"
        >
          <DeleteIcon color="error" fontSize="small" />
        </IconButton>
        {showSwapLeft ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSwapLeft?.();
            }}
            sx={{
              position: 'absolute',
              left: compact ? 1 : 2,
              bottom: compact ? 1 : 2,
              bgcolor: 'rgba(255,255,255,0.92)',
              p: 0.2,
              '&:hover': { bgcolor: '#fff' }
            }}
            aria-label="Sola taşı"
          >
            <ChevronLeftIcon sx={{ fontSize: navIcon }} />
          </IconButton>
        ) : null}
        {showSwapRight ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSwapRight?.();
            }}
            sx={{
              position: 'absolute',
              right: compact ? 1 : 2,
              bottom: compact ? 1 : 2,
              bgcolor: 'rgba(255,255,255,0.92)',
              p: 0.2,
              '&:hover': { bgcolor: '#fff' }
            }}
            aria-label="Sağa taşı"
          >
            <ChevronRightIcon sx={{ fontSize: navIcon }} />
          </IconButton>
        ) : null}
      </>
    ) : (
      <label
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          padding: compact ? 4 : 6
        }}
      >
        <CloudUploadIcon sx={{ fontSize: iconFont, color: accentOrange, flexShrink: 0, mb: compact ? 0.2 : 0.35 }} />
        <Typography
          sx={{
            fontSize: labelFont,
            fontWeight: 700,
            color: '#78716c',
            textAlign: 'center',
            lineHeight: 1.25,
            px: 0.25,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word'
          }}
        >
          {label}
        </Typography>
        <input type="file" accept="image/*" hidden onChange={onChange} onClick={(e) => { e.target.value = null; }} />
      </label>
    )}
  </Box>
  );
};

function Urunekle() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sube = queryParams.get('sube') || 'Bilinmeyen Şube';

  const initialFormState = {
    isim: '',
    kategori: SABIT_KATEGORI,
    urunTipi: '',
    aciklama: '',
    gram: '',
    uretici: '',
    urunMilyem: '',
    iscilik: '',
    adetIscilik: '',
    stok: '',
    siteLinki: '',
    pazaryeriLinki: '',
    ikasCategoryId: '',
    ikasCategoryIds: [],
    ikasCategoryNamesSnapshot: [],
    urunKategorisi: '',
    urunCapi: '',
    tasOzelligi: '',
    zincirUzunlugu: '',
    urunRengi: ''
  };

  const [form, setForm] = useState(initialFormState);
  const [imageSlots, setImageSlots] = useState(makeEmptyImageSlots);
  const [trendyolImageSlots, setTrendyolImageSlots] = useState(makeEmptyImageSlots);
  const [trendyolConfig, setTrendyolConfig] = useState({});
  const [trendyolConfigFetched, setTrendyolConfigFetched] = useState(false);
  const [trendyolCategorySearchPortalEl, setTrendyolCategorySearchPortalEl] = useState(null);
  const dragFromIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);

  const [hata, setHata] = useState('');
  const [basari, setBasari] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [firmalar, setFirmalar] = useState([]);
  const [ikasCategories, setIkasCategories] = useState([]);
  const [trendyolInfo, setTrendyolInfo] = useState(() => buildUrunEkleTrendyolShipmentBase({}));

  const ukbaTrendyolBrandId = useMemo(() => resolveUkbaTrendyolBrandId(trendyolConfig), [trendyolConfig]);

  /** Marka seçici controlled uyumu: ukbaTrendyolBrandId gelince önce state dolar */
  useLayoutEffect(() => {
    if (!ukbaTrendyolBrandId) return;
    setTrendyolInfo((prev) => {
      const cur = rejectDeprecatedTestTrendyolBrandId(String(prev.ty_brandId ?? '').trim());
      if (cur) return prev;
      return {
        ...prev,
        ty_brandId: ukbaTrendyolBrandId,
        ty_brandName: UKBA_GOLD_TRENDYOL_MARKA_ADI
      };
    });
  }, [ukbaTrendyolBrandId]);

  useEffect(() => {
    if (!sube || sube === 'Bilinmeyen Şube') {
      setHata('Şube bilgisi eksik. Lütfen şube seçimi yapınız.');
    }
  }, [sube]);

  const getBackendAuthHeaders = async () => {
    const headers = {};
    const sharedSecret = String(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET || '').trim();
    if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
    try {
      const token = await auth.currentUser?.getIdToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (e) {
      // Sessiz fallback: kategori alanı legacy giriş gibi kullanılabilir.
    }
    return headers;
  };

  useEffect(() => {
    let mounted = true;
    const loadTyConfig = async () => {
      try {
        const response = await fetch(apiUrl('/api/trendyol/config'), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) return;
        if (mounted && data?.config && typeof data.config === 'object') setTrendyolConfig(data.config);
      } catch (e) {
        /* no-op */
      } finally {
        if (mounted) setTrendyolConfigFetched(true);
      }
    };
    loadTyConfig();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadIkasCategories = async () => {
      try {
        const response = await fetch(apiUrl('/api/ikas/categories'), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) return;
        const rows = Array.isArray(data?.categories) ? data.categories : [];
        if (mounted) setIkasCategories(rows);
      } catch (e) {
        // no-op
      }
    };
    loadIkasCategories();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!sube || sube === 'Bilinmeyen Şube') return;
    const q = query(collection(db, 'subeler', sube, 'firmalar'), orderBy('firmaIsmi', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firmaListesi = snapshot.docs.map((d) => d.data().firmaIsmi).filter(Boolean);
      setFirmalar(firmaListesi);
    });
    return () => unsubscribe();
  }, [sube]);

  const handleChange = (e) => {
    const { name, value: raw } = e.target;
    let value = raw;

    switch (name) {
      case 'urunMilyem':
      case 'iscilik':
      case 'adetIscilik':
        value = filterDecimalNumber(raw);
        break;
      case 'tasOzelligi':
      case 'urunRengi':
        value = filterLettersSpacesUpperTR(raw);
        break;
      case 'urunCapi':
      case 'zincirUzunlugu':
        value = filterAlnumSpacesUpperTR(raw);
        break;
      default:
        value = raw;
    }

    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Bir üründe ya işçilik ya adet işçiliği
      if (name === 'iscilik' && String(value || '').trim()) {
        next.adetIscilik = '';
      }
      if (name === 'adetIscilik' && String(value || '').trim()) {
        next.iscilik = '';
      }
      return next;
    });
  };

  const handleTrendyolShipmentFieldChange = (e) => {
    const { name, value } = e.target;
    setTrendyolInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategorySelect = (categoryId) => {
    const normalizeId = (v) => String(v || '').trim();
    const normalizeName = (v) =>
      String(v || '')
        .trim()
        .toLocaleLowerCase('tr-TR')
        .replace(/\s+/g, ' ');
    const byId = new Map(
      ikasCategories.map((cat) => [normalizeId(cat?.id), { id: normalizeId(cat?.id), name: String(cat?.name || '').trim(), parentId: normalizeId(cat?.parentId) }])
    );
    const seedIds = Array.isArray(categoryId) ? categoryId.map(normalizeId) : [normalizeId(categoryId)];
    const selectedSet = new Set(seedIds.filter(Boolean));

    const addParents = (id) => {
      const visited = new Set();
      let currentId = id;
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const row = byId.get(currentId);
        const parentId = normalizeId(row?.parentId);
        if (!parentId || !byId.has(parentId)) break;
        selectedSet.add(parentId);
        currentId = parentId;
      }
    };

    [...selectedSet].forEach(addParents);

    const manualParentByChildName = {
      'bebek & çocuk küpe': 'bebek & çocuk',
      'bebek & çocuk künye': 'bebek & çocuk',
      'kolye ucu': 'kolye',
      'zincir': 'kolye',
      'harf kolye': 'kolye',
      'tuğrali kolye': 'kolye',
      'tuğralı kolye': 'kolye',
      'külçe kolye': 'kolye',
      'tasarim kolye': 'kolye',
      'tasarım kolye': 'kolye',
      'minimal yüzük': 'yüzük',
      'tasarim yüzük': 'yüzük',
      'tasarım yüzük': 'yüzük',
      'minimal bileklik': 'bileklik',
      'top küpe': 'küpe'
    };
    const idByName = new Map(
      [...byId.values()].map((row) => [normalizeName(row?.name), row?.id]).filter(([name, id]) => !!name && !!id)
    );
    [...selectedSet].forEach((id) => {
      const childName = normalizeName(byId.get(id)?.name);
      const parentName = manualParentByChildName[childName];
      const parentId = parentName ? idByName.get(parentName) : '';
      if (parentId) selectedSet.add(parentId);
    });
    [...selectedSet].forEach(addParents);

    const finalIds = [...selectedSet];
    const finalNames = finalIds
      .map((id) => byId.get(id)?.name || '')
      .map((x) => String(x || '').trim())
      .filter(Boolean);

    setForm((prev) => ({
      ...prev,
      ikasCategoryIds: finalIds,
      ikasCategoryNamesSnapshot: finalNames,
      ikasCategoryId: finalIds[0] || '',
      urunKategorisi: finalNames[0] || ''
    }));
  };

  const handleFileChange = (e, slotIndex) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setHata('Görsel boyutu 2MB’ı geçemez.');
        return;
      }
      setHata('');
      setImageSlots((prev) => {
        const next = prev.map((s) => ({ ...s }));
        const old = next[slotIndex];
        if (old?.preview && String(old.preview).startsWith('blob:')) {
          URL.revokeObjectURL(old.preview);
        }
        next[slotIndex] = { file, preview: URL.createObjectURL(file) };
        return next;
      });
    }
  };

  const handleRemoveImage = (slotIndex) => {
    setImageSlots((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const old = next[slotIndex];
      if (old?.preview && String(old.preview).startsWith('blob:')) {
        URL.revokeObjectURL(old.preview);
      }
      next[slotIndex] = { file: null, preview: null };
      return next;
    });
  };

  const handleTrendyolFileChange = (e, slotIndex) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setHata('Görsel boyutu 2MB’ı geçemez.');
        return;
      }
      setHata('');
      setTrendyolImageSlots((prev) => {
        const next = prev.map((s) => ({ ...s }));
        const old = next[slotIndex];
        if (old?.preview && String(old.preview).startsWith('blob:')) {
          URL.revokeObjectURL(old.preview);
        }
        next[slotIndex] = { file, preview: URL.createObjectURL(file) };
        return next;
      });
    }
  };

  const handleRemoveTrendyolImage = (slotIndex) => {
    setTrendyolImageSlots((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const old = next[slotIndex];
      if (old?.preview && String(old.preview).startsWith('blob:')) {
        URL.revokeObjectURL(old.preview);
      }
      next[slotIndex] = { file: null, preview: null };
      return next;
    });
  };

  const moveSlot = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= GORSEL_SLOT_COUNT || toIndex >= GORSEL_SLOT_COUNT) return;
    setImageSlots((prev) => {
      const next = prev.map((s) => ({ ...s, file: s.file, preview: s.preview }));
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const swapAdjacentSlots = (a, b) => {
    if (a < 0 || b < 0 || a >= GORSEL_SLOT_COUNT || b >= GORSEL_SLOT_COUNT) return;
    setImageSlots((prev) => {
      const next = prev.map((s) => ({ ...s, file: s.file, preview: s.preview }));
      const t = next[a];
      next[a] = next[b];
      next[b] = t;
      return next;
    });
  };

  const uploadGorsel = async (file, barkod, slot) => {
    if (!file) return null;
    const storageRef = ref(storage, `subeler/${sube}/urunler/${barkod}/${slot}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHata('');
    setBasari('');
    if (!String(form.urunTipi || '').trim()) {
      setHata('Ürün tipi seçimi zorunludur.');
      return;
    }
    setYukleniyor(true);

    try {
      const barkod = generateUrunBarkod(sube);

      const gorselURL1 = await uploadGorsel(imageSlots[0]?.file, barkod, 'gorsel1');
      const gorselURL2 = await uploadGorsel(imageSlots[1]?.file, barkod, 'gorsel2');
      const gorselURL3 = await uploadGorsel(imageSlots[2]?.file, barkod, 'gorsel3');
      const gorselURL4 = await uploadGorsel(imageSlots[3]?.file, barkod, 'gorsel4');
      const gorselURL5 = await uploadGorsel(imageSlots[4]?.file, barkod, 'gorsel5');

      const ty_gorselURL1 = await uploadGorsel(trendyolImageSlots[0]?.file, barkod, 'ty_gorsel1');
      const ty_gorselURL2 = await uploadGorsel(trendyolImageSlots[1]?.file, barkod, 'ty_gorsel2');
      const ty_gorselURL3 = await uploadGorsel(trendyolImageSlots[2]?.file, barkod, 'ty_gorsel3');
      const ty_gorselURL4 = await uploadGorsel(trendyolImageSlots[3]?.file, barkod, 'ty_gorsel4');
      const ty_gorselURL5 = await uploadGorsel(trendyolImageSlots[4]?.file, barkod, 'ty_gorsel5');

      const urunData = {
        ...buildUrunCreatePayload({
          form,
          barkod,
          sube,
          trendyolInfo,
          trendyolConfig,
          imageUrls: {
            gorselURL1,
            gorselURL2,
            gorselURL3,
            gorselURL4,
            gorselURL5,
            ty_gorselURL1,
            ty_gorselURL2,
            ty_gorselURL3,
            ty_gorselURL4,
            ty_gorselURL5
          }
        }),
        timestamp: serverTimestamp()
      };

      await setDoc(doc(db, `subeler/${sube}/urunler`, barkod), urunData);
      const successMessage = `"${form.isim || 'Ürün'}" başarıyla eklendi!`;
      setBasari(successMessage);
      toast.success(successMessage, {
        position: 'top-right',
        autoClose: 2000
      });
      handleReset();
    } catch (err) {
      setHata(`Ürün eklenemedi: ${err.message}`);
      console.error('Hata:', err);
    } finally {
      setYukleniyor(false);
    }
  };

  const handleReset = () => {
    setForm({ ...initialFormState });
    imageSlots.forEach((s) => {
      if (s?.preview && String(s.preview).startsWith('blob:')) URL.revokeObjectURL(s.preview);
    });
    trendyolImageSlots.forEach((s) => {
      if (s?.preview && String(s.preview).startsWith('blob:')) URL.revokeObjectURL(s.preview);
    });
    setImageSlots(makeEmptyImageSlots());
    setTrendyolImageSlots(makeEmptyImageSlots());
    setHata('');
    setTrendyolInfo(buildUrunEkleTrendyolShipmentBase(trendyolConfig));
    dragFromIndexRef.current = null;
    setDragOverIndex(null);
    setDraggingIndex(null);
  };

  /** overflow-x:auto + overflow-y:visible kombinasyonu birçok tarayıcıda etiketi kırpar; içerik bu satır kadar ise taşmadan görünsün */
  const genelBilgilerSatirOuterSx = {
    width: '100%',
    mb: 1.75,
    overflowX: 'visible',
    overflowY: 'visible',
    pt: 0.75,
    boxSizing: 'border-box'
  };
  /** Genel bilgiler: 5 sütun × 2 satır (İşçilik + Adet İşçiliği aynı satırda) */
  const genelBilgilerSatirGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(5, minmax(0, 1fr))'
    },
    columnGap: { xs: 1, md: 1.15 },
    rowGap: { xs: 1.1, md: 1.25 },
    width: '100%',
    minWidth: 0,
    alignItems: 'start',
    boxSizing: 'border-box',
    '& > .MuiTextField-root, & > .MuiAutocomplete-root': { minWidth: 0, overflow: 'visible' }
  };
  /** Ürün detayı + Trendyol gönderim: 5 sütun × 2 satır */
  const urunDetaylariOuterSx = {
    width: '100%',
    mb: 1.35,
    overflowX: 'visible',
    overflowY: 'visible',
    pt: 0.85,
    pb: 0.35,
    boxSizing: 'border-box'
  };
  const urunDetaylariGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(5, minmax(0, 1fr))'
    },
    columnGap: { xs: 1, md: 1.15 },
    rowGap: { xs: 1.1, md: 1.25 },
    width: '100%',
    minWidth: 0,
    alignItems: 'start',
    boxSizing: 'border-box',
    '& > *': { minWidth: 0 }
  };
  /** Grid hücrelerinde outlined etiket kesilmesin */
  const urunDetayGridFieldSx = {
    ...fieldSx,
    flex: 'none',
    width: '100%',
    maxWidth: 'none',
    minWidth: 0,
    '& .MuiFormControl-root': { overflow: 'visible' },
    '& .MuiOutlinedInput-root': {
      ...(fieldSx['& .MuiOutlinedInput-root'] || {}),
      overflow: 'visible'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      overflow: 'visible'
    },
    '& .MuiInputLabel-root': {
      ...(fieldSx['& .MuiInputLabel-root'] || {}),
      zIndex: 1,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      lineHeight: 1.2,
      fontSize: '0.72rem'
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      maxWidth: '100%'
    }
  };

  const hasTrendyolProductImage = trendyolImageSlots.some((slot) => Boolean(slot?.file || slot?.preview));

  /** Web ve Trendyol görsel alanları: aynı panel stili, eşit sütun, satır kart genişliğini doldurur */
  const imageDropzonePanelSx = {
    p: 0.85,
    borderRadius: '12px',
    bgcolor: '#faf7f2',
    border: '1px solid #e7e5e4',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
    alignSelf: 'stretch',
    boxSizing: 'border-box'
  };
  const imagePairGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: { xs: 1, md: 1.35 },
    rowGap: 1.15,
    width: '100%',
    mb: 0,
    alignItems: 'stretch',
    boxSizing: 'border-box'
  };
  /** Her iki blokta slot genişlikleri birbirinin aynısı olsun (yan yana iki eşit sütunda aynı kutu boyutu üretilir) */
  const imageSlotRowSx = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 0.5,
    width: '100%',
    alignItems: 'stretch',
    overflowX: { xs: 'auto', md: 'visible' },
    WebkitOverflowScrolling: 'touch',
    boxSizing: 'border-box',
    '& > *': { minWidth: 0 }
  };
  const imageSectionColumnSx = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
    height: '100%',
    alignSelf: 'stretch'
  };
  const imageSectionHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.65,
    mb: 0.55,
    minHeight: 30
  };
  const imageSectionTitleSx = { fontWeight: 800, fontSize: '0.92rem', color: '#0f172a', lineHeight: 1.2 };

  return (
    <Box
      className="ue-page"
      sx={{
        width: '100%',
        backgroundColor: '#eef1f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        py: 0.5,
        px: { xs: 1, sm: 1.5, md: 2 },
        position: 'relative',
        boxSizing: 'border-box',
        minHeight: 0,
        alignSelf: 'stretch'
      }}
    >
      <Card
        id="urun-ekle-kart"
        className="ue-card"
        sx={{
          width: '100%',
          maxWidth: 'none',
          borderRadius: '14px',
          boxShadow: '0 10px 40px rgba(15,23,42,0.08)',
          border: '1px solid #e2e8f0',
          overflow: 'visible'
        }}
      >
        <Box
          className="ue-header"
          sx={{
            background: headerGradient,
            px: { xs: 2, sm: 2.75 },
            py: 1.35,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5
          }}
        >
          <Stack direction="row" spacing={1.15} alignItems="center" flexWrap="wrap">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              <InventoryIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.12rem', lineHeight: 1.2 }}>
                Yeni ürün ekle
              </Typography>
            </Box>
          </Stack>
          <Button
            className="ue-reset-btn"
            variant="outlined"
            onClick={handleReset}
            startIcon={<RefreshIcon />}
            size="small"
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.55)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Temizle
          </Button>
        </Box>

        <CardContent className="ue-content" sx={{ p: { xs: 1.5, sm: 2 }, pb: { xs: 2, sm: 2.25 }, bgcolor: '#fff', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'visible' }}>
          {hata && <Alert severity="error" sx={{ mb: 2 }}>{hata}</Alert>}
          {basari && <Alert severity="success" sx={{ mb: 2 }}>{basari}</Alert>}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.1 }}>
              <InfoIcon sx={{ ...sectionIconSx, fontSize: 22 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', color: '#0f172a' }}>Genel bilgiler</Typography>
            </Box>
            <Box className="ue-genel-outer" sx={genelBilgilerSatirOuterSx}>
              <Box className="ue-genel-grid" sx={genelBilgilerSatirGridSx}>
              <TextField
                fullWidth
                label="Kategori"
                value={SABIT_KATEGORI}
                variant="outlined"
                size="small"
                sx={{
                  ...genelBilgilerGridFieldSx,
                  '& .MuiInputBase-input': { cursor: 'default', color: '#0f172a', fontWeight: 600 }
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                select
                required
                label="Ürün tipi"
                name="urunTipi"
                value={form.urunTipi}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={{
                  ...genelBilgilerGridFieldSx,
                  '& .MuiSelect-select': { fontStyle: 'normal', fontFamily: 'inherit' }
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => (v ? String(v) : ''),
                  MenuProps: {
                    PaperProps: {
                      sx: { '& .MuiMenuItem-root': { fontSize: '0.95rem', py: 1, fontStyle: 'normal' } }
                    }
                  }
                }}
              >
                <MenuItem value="" sx={{ display: 'none' }}>
                  {'\u200b'}
                </MenuItem>
                {TAKI_URUN_TIPLERI.map((urun) => (
                  <MenuItem key={urun} value={urun}>
                    {urun}
                  </MenuItem>
                ))}
              </TextField>
              {ikasCategories.length > 0 ? (
                <TextField
                  select
                  label="Ürün kategorisi"
                  value={Array.isArray(form.ikasCategoryIds) ? form.ikasCategoryIds : []}
                  onChange={(e) => handleCategorySelect(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const ids = Array.isArray(selected) ? selected.filter(Boolean) : [];
                      if (ids.length === 0) return '';
                      const names = ids
                        .map((id) => ikasCategories.find((c) => String(c?.id) === String(id))?.name)
                        .filter(Boolean);
                      return names.join(', ');
                    }
                  }}
                  sx={genelBilgilerGridFieldSx}
                >
                  {ikasCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  label="Ürün kategorisi"
                  name="urunKategorisi"
                  value={form.urunKategorisi}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  sx={genelBilgilerGridFieldSx}
                />
              )}
              <TextField
                fullWidth
                label="Ürün adı"
                name="isim"
                value={form.isim}
                onChange={handleChange}
                required
                placeholder="Örn: Bilezik"
                variant="outlined"
                size="small"
                sx={genelBilgilerGridFieldSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Gram"
                name="gram"
                type="number"
                value={form.gram}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={genelBilgilerGridFieldSx}
                InputLabelProps={{ shrink: true }}
                InputProps={{ endAdornment: <InputAdornment position="end">gr</InputAdornment> }}
              />
              <TextField
                fullWidth
                label="Stok"
                name="stok"
                type="number"
                value={form.stok}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={genelBilgilerGridFieldSx}
                InputLabelProps={{ shrink: true }}
                InputProps={{ endAdornment: <InputAdornment position="end">Adet</InputAdornment> }}
              />
              <Autocomplete
                fullWidth
                freeSolo
                options={firmalar}
                value={form.uretici}
                onChange={(event, newValue) => {
                  const v = newValue == null ? '' : String(newValue);
                  setForm((prev) => ({ ...prev, uretici: v }));
                }}
                onInputChange={(event, newInputValue) => {
                  setForm((prev) => ({ ...prev, uretici: newInputValue ?? '' }));
                }}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Üretici"
                    placeholder="Ahlatçı"
                    variant="outlined"
                    size="small"
                    sx={genelBilgilerGridFieldSx}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  />
                )}
                sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              />
              <TextField
                fullWidth
                label="Milyem"
                name="urunMilyem"
                value={form.urunMilyem}
                onChange={handleChange}
                placeholder="0.916"
                variant="outlined"
                size="small"
                sx={genelBilgilerGridFieldSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="İşçilik"
                name="iscilik"
                value={form.iscilik}
                onChange={handleChange}
                placeholder="0.05"
                variant="outlined"
                size="small"
                sx={genelBilgilerGridFieldSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Adet İşçiliği"
                name="adetIscilik"
                value={form.adetIscilik}
                onChange={handleChange}
                placeholder="0.25"
                variant="outlined"
                size="small"
                sx={genelBilgilerGridFieldSx}
                InputLabelProps={{ shrink: true }}
              />
              </Box>
            </Box>

            <Box className="ue-detail-outer" sx={urunDetaylariOuterSx}>
              <Box className="ue-detail-grid" sx={urunDetaylariGridSx}>
              {trendyolConfigFetched && !ukbaTrendyolBrandId ? (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    Trendyol Marka ID ayarlardan tanımlı değil.
                  </Alert>
                </Box>
              ) : null}
              <Box
                ref={setTrendyolCategorySearchPortalEl}
                sx={{
                  ...urunDetayGridFieldSx,
                  minWidth: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start'
                }}
              />
              <TextField
                label="Ürün çapı"
                name="urunCapi"
                value={form.urunCapi}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={urunDetayGridFieldSx}
              />
              <TextField
                label="Taş özelliği"
                name="tasOzelligi"
                value={form.tasOzelligi}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={urunDetayGridFieldSx}
              />
              <TextField
                label="Zincir uzunluğu"
                name="zincirUzunlugu"
                value={form.zincirUzunlugu}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={urunDetayGridFieldSx}
              />
              <TextField
                label="Ürün rengi"
                name="urunRengi"
                value={form.urunRengi}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={urunDetayGridFieldSx}
              />
                <TextField
                  select
                  fullWidth
                  label="KDV oranı"
                  name="ty_vatRate"
                  value={
                    trendyolInfo.ty_vatRate != null && trendyolInfo.ty_vatRate !== ''
                      ? String(trendyolInfo.ty_vatRate)
                      : '0'
                  }
                  onChange={handleTrendyolShipmentFieldChange}
                  variant="outlined"
                  size="small"
                  sx={urunDetayGridFieldSx}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } }
                  }}
                >
                  {['0', '1', '10', '20'].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      %{opt}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  label="Desi"
                  name="ty_dimensionalWeight"
                  type="number"
                  value={
                    trendyolInfo.ty_dimensionalWeight != null && trendyolInfo.ty_dimensionalWeight !== ''
                      ? trendyolInfo.ty_dimensionalWeight
                      : '1'
                  }
                  onChange={handleTrendyolShipmentFieldChange}
                  variant="outlined"
                  size="small"
                  sx={urunDetayGridFieldSx}
                />
                <TextField
                  select
                  fullWidth
                  label="Kargo firması"
                  name="ty_cargoCompanyId"
                  value={
                    trendyolInfo.ty_cargoCompanyId != null && trendyolInfo.ty_cargoCompanyId !== ''
                      ? String(trendyolInfo.ty_cargoCompanyId)
                      : TRENDYOL_URUN_EKLE_DEFAULT_CARGO_COMPANY_ID
                  }
                  onChange={handleTrendyolShipmentFieldChange}
                  variant="outlined"
                  size="small"
                  sx={urunDetayGridFieldSx}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } }
                  }}
                >
                  {TRENDYOL_URUN_EKLE_CARGO_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Teslimat süresi"
                  name="ty_deliveryDuration"
                  value={trendyolInfo.ty_deliveryDuration ?? ''}
                  onChange={handleTrendyolShipmentFieldChange}
                  variant="outlined"
                  size="small"
                  sx={urunDetayGridFieldSx}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) =>
                      selected !== '' && selected != null ? `${selected} gün` : '',
                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } }
                  }}
                >
                  <MenuItem value="" sx={{ display: 'none' }}>
                    {'\u200b'}
                  </MenuItem>
                  {['1', '2', '3', '4', '5'].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt} gün
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Trendyol markası"
                  name="ty_brandId"
                  disabled={!ukbaTrendyolBrandId}
                  value={String(trendyolInfo.ty_brandId ?? (ukbaTrendyolBrandId || ''))}
                  onChange={(e) =>
                    setTrendyolInfo((prev) => ({
                      ...prev,
                      ty_brandId: String(e.target.value),
                      ty_brandName: UKBA_GOLD_TRENDYOL_MARKA_ADI
                    }))
                  }
                  variant="outlined"
                  size="small"
                  sx={urunDetayGridFieldSx}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: !ukbaTrendyolBrandId,
                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } }
                  }}
                >
                  {!ukbaTrendyolBrandId ? (
                    <MenuItem value="" disabled>
                      Trendyol Marka ID ayarlardan tanımlı değil.
                    </MenuItem>
                  ) : (
                    <MenuItem value={ukbaTrendyolBrandId}>{UKBA_GOLD_TRENDYOL_MARKA_ADI}</MenuItem>
                  )}
                </TextField>
              </Box>
            </Box>

            <Box
              className="ue-trendyol-box"
              sx={{
                mb: 1.35,
                p: { xs: 0.95, sm: 1.1 },
                borderRadius: '12px',
                bgcolor: '#fffdfb',
                border: '1px solid #fde8d7',
                boxShadow: '0 1px 0 rgba(15,23,42,0.04)',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <TrendyolProductInfoForm
                variant="inline"
                hideAutoFields
                mergedCategoryShipmentLayout
                trendyolCategorySearchPortalEl={trendyolCategorySearchPortalEl}
                showValidation
                hideTrendyolDescription
                urunEkleSimplifiedTrendyol
                suppressBrandIdField
                value={trendyolInfo}
                onChange={setTrendyolInfo}
                product={{
                  ...form,
                  ...trendyolConfig,
                  hasTrendyolProductImage,
                  defaultVatRate: '0',
                  defaultDimensionalWeight: '1',
                  defaultCargoCompanyId: TRENDYOL_URUN_EKLE_DEFAULT_CARGO_COMPANY_ID,
                  defaultBrandId: ukbaTrendyolBrandId
                }}
              />
            </Box>

            <Box className="ue-image-pair" sx={imagePairGridSx}>
              <Box sx={imageSectionColumnSx}>
                <Box sx={imageSectionHeaderSx}>
                  <ImageIcon sx={{ ...sectionIconSx, fontSize: 22 }} />
                  <Typography sx={imageSectionTitleSx}>Web Görselleri</Typography>
                </Box>
                <Box sx={imageDropzonePanelSx}>
                  <Box className="ue-image-slot-row" sx={imageSlotRowSx}>
                    {imageSlots.map((slot, index) => (
                      <ImageUploadBox
                        key={index}
                        compact
                        stretchInRow
                        label={`Görsel ${index + 1}`}
                        preview={slot.preview}
                        onChange={(e) => handleFileChange(e, index)}
                        onRemove={() => handleRemoveImage(index)}
                        draggable={Boolean(slot.preview)}
                        isDragging={draggingIndex === index}
                        isDragOver={dragOverIndex === index}
                        onDragStart={(e) => {
                          if (!slot.preview) {
                            e.preventDefault();
                            return;
                          }
                          dragFromIndexRef.current = index;
                          setDraggingIndex(index);
                          try {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(index));
                          } catch (err) {
                            /* ignore */
                          }
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverIndex(index);
                        }}
                        onDragLeave={() => {
                          setDragOverIndex((prev) => (prev === index ? null : prev));
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromStr = dragFromIndexRef.current != null ? String(dragFromIndexRef.current) : e.dataTransfer.getData('text/plain');
                          const from = Number(fromStr);
                          if (Number.isFinite(from)) moveSlot(from, index);
                          dragFromIndexRef.current = null;
                          setDragOverIndex(null);
                          setDraggingIndex(null);
                        }}
                        onDragEnd={() => {
                          dragFromIndexRef.current = null;
                          setDragOverIndex(null);
                          setDraggingIndex(null);
                        }}
                        showSwapLeft={Boolean(slot.preview) && index > 0}
                        showSwapRight={Boolean(slot.preview) && index < GORSEL_SLOT_COUNT - 1}
                        onSwapLeft={() => swapAdjacentSlots(index, index - 1)}
                        onSwapRight={() => swapAdjacentSlots(index, index + 1)}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box sx={imageSectionColumnSx}>
                <Box sx={imageSectionHeaderSx}>
                  <ImageIcon sx={{ ...sectionIconSx, fontSize: 22 }} />
                  <Typography sx={imageSectionTitleSx}>Trendyol Görselleri</Typography>
                </Box>
                <Box sx={imageDropzonePanelSx}>
                  <Box className="ue-image-slot-row" sx={imageSlotRowSx}>
                    {trendyolImageSlots.map((slot, index) => (
                      <ImageUploadBox
                        key={`ty-${index}`}
                        compact
                        stretchInRow
                        label={`Görsel ${index + 1}`}
                        preview={slot.preview}
                        onChange={(e) => handleTrendyolFileChange(e, index)}
                        onRemove={() => handleRemoveTrendyolImage(index)}
                        draggable={false}
                        showSwapLeft={false}
                        showSwapRight={false}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              className="ue-submit-bar"
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 4,
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mt: '12px',
                pt: '10px',
                pb: { xs: 1.15, sm: 1.35 },
                bgcolor: 'rgba(255,255,255,0.94)',
                backdropFilter: 'blur(10px)',
                borderTop: '1px solid #e8ecf2'
              }}
            >
              <Button
                className="ue-submit-btn"
                type="submit"
                variant="contained"
                size="large"
                disabled={yukleniyor}
                startIcon={!yukleniyor ? <SaveIcon sx={{ color: '#fff' }} /> : null}
                sx={{
                  background: buttonGradient,
                  color: '#fff',
                  minWidth: 260,
                  maxWidth: '100%',
                  height: 46,
                  fontWeight: 800,
                  fontSize: '0.98rem',
                  textTransform: 'none',
                  borderRadius: '10px',
                  boxShadow: '0 6px 20px rgba(124,45,18,0.35)',
                  '&:hover': { opacity: 0.96, boxShadow: '0 8px 24px rgba(124,45,18,0.45)' }
                }}
              >
                {yukleniyor ? <CircularProgress size={24} color="inherit" /> : 'Kaydet ve oluştur'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Fab
        className="ue-fab"
        size="medium"
        aria-label="Formun başına git"
        onClick={() => document.getElementById('urun-ekle-kart')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          bgcolor: '#0f172a',
          color: '#fff',
          '&:hover': { bgcolor: '#1e293b' }
        }}
      >
        <AddIcon />
      </Fab>
      <style>{`
        /* --- MOBİL: Ürün Ekle (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 600px) {
          .ue-page {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            overflow-x: hidden !important;
            padding: 4px 6px 88px !important;
            box-sizing: border-box !important;
          }

          .ue-card {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: hidden !important;
            border-radius: 12px !important;
          }

          .ue-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            padding-left: 14px !important;
            padding-right: 14px !important;
          }

          .ue-reset-btn {
            width: 100% !important;
            min-height: 40px !important;
          }

          /* Sag alttaki Hizli Araclar dugmesinin ustune biniyordu;
             telefonda zaten parmakla kaydirmak yeterli. */
          .ue-fab {
            display: none !important;
          }

          .ue-content {
            padding: 12px 8px 16px !important;
            overflow-x: hidden !important;
          }

          .ue-genel-outer,
          .ue-detail-outer {
            overflow-x: hidden !important;
            overflow-y: visible !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Kisa alanlar ikiserli diziliyor: tek sutunda form 2.4 ekran
             suruyordu, telefonda gereksiz kaydirma oluyordu. */
          .ue-genel-grid,
          .ue-detail-grid {
            grid-template-columns: 1fr 1fr !important;
            column-gap: 8px !important;
            row-gap: 8px !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .ue-genel-grid > .MuiTextField-root,
          .ue-genel-grid > .MuiAutocomplete-root,
          .ue-detail-grid > .MuiTextField-root,
          .ue-detail-grid > .MuiBox-root {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          /* Uzun yazilan alanlar (kategori, urun adi) tam satir kalsin */
          .ue-genel-grid > *:nth-child(1),
          .ue-genel-grid > *:nth-child(4),
          .ue-detail-grid > .MuiBox-root {
            grid-column: 1 / -1 !important;
          }

          .ue-page .MuiInputBase-input,
          .ue-page .MuiSelect-select {
            font-size: 16px !important;
          }

          .ue-trendyol-box {
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
            padding: 10px 8px !important;
            box-sizing: border-box !important;
          }

          .ue-trendyol-box .ty-attr-grid-row {
            grid-template-columns: 1fr !important;
            row-gap: 8px !important;
          }

          .ue-trendyol-box .MuiChip-root {
            max-width: 100% !important;
          }

          .ue-image-pair {
            grid-template-columns: 1fr !important;
            row-gap: 14px !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .ue-image-slot-row {
            flex-wrap: wrap !important;
            overflow-x: hidden !important;
            gap: 6px !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .ue-image-slot-row > .ue-image-slot {
            flex: 1 1 calc(33.333% - 6px) !important;
            width: calc(33.333% - 6px) !important;
            min-width: calc(33.333% - 6px) !important;
            max-width: calc(33.333% - 6px) !important;
            height: auto !important;
            min-height: 0 !important;
            aspect-ratio: 1 !important;
          }

          .ue-submit-bar {
            position: sticky !important;
            bottom: 0 !important;
            margin-left: -8px !important;
            margin-right: -8px !important;
            width: calc(100% + 16px) !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            box-sizing: border-box !important;
          }

          .ue-submit-btn {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            min-height: 46px !important;
          }

          .ue-fab {
            right: 16px !important;
            bottom: 88px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default Urunekle;
